import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "safari",
  app: "Safari",
  title: "How to block websites on iPhone when the app is gone",
  h1: "How to block a website, not just an app",
  description:
    "Why deleting an app moves the habit to Safari, and the Content Restrictions setting that closes the door properly — including on the browser itself.",
  targets: [
    "how to block websites on iphone",
    "block safari on iphone",
    "blocked app still works in browser",
    "iphone content restrictions websites",
  ],
  published: "2026-09-03",
  standfirst:
    "Every app on this site has a website, and the website is where the habit goes about a day after you delete the app.",
  takeaways: [
    "App Limits do not cover the web. Blocking TikTok, Reddit or X as apps leaves all three fully usable in Safari.",
    "Content & Privacy Restrictions → Web Content → Never Allow is the setting that closes it, and it applies across every browser on the phone.",
    "Deleting Safari does not work — installing another browser takes ten seconds. Restricting content does work, because it operates below the browser.",
  ],
  mechanic: [
    "This page is about a gap rather than an app. Apple's Screen Time has two systems that look similar and are not: App Limits, which counts time in apps, and Content & Privacy Restrictions, which controls what the phone will load. Almost everybody uses the first and almost nobody uses the second, and the first has a hole in it the size of the web.",
    "The consequence is that a carefully limited phone still opens x.com, reddit.com, the mobile version of Instagram and a browser tab that plays exactly the same short videos. Nothing has been circumvented; the limit simply never applied. Safari is not a social app and Screen Time does not treat it as one.",
    "The behavioural half is that this is usually not a deliberate workaround. Somebody deletes an app on Sunday, meaning it, and by Wednesday has typed three letters into a browser address bar without a decision being made anywhere. The address bar completes it, the session happens, and the person concludes they have no willpower — when what actually happened is that they closed one door in a room with two.",
  ],
  limitFails:
    "An App Limit on Safari is close to useless, because Safari is a genuine tool: you will need it for a train time, a form, a bank, an address. So the limit either gets set generously enough to be irrelevant or it fires while somebody is trying to do something ordinary, and gets dismissed. The instrument that works is not a time budget on the browser but a restriction on specific addresses, which leaves the browser fully functional for everything except the four sites you named.",
  instead: [
    "Settings → Screen Time → Content & Privacy Restrictions → turn it on → Content Restrictions → Web Content → Limit Adult Websites, which enables the Never Allow list.",
    "Add every site whose app you have already blocked. The list is per-address, so add both x.com and twitter.com, and both reddit.com and old.reddit.com.",
    "Set the Screen Time passcode if you have not — without it the restriction is three taps from being switched off.",
    "Do not delete Safari. A restriction applies to whatever browser is installed; a deleted Safari is replaced in ten seconds by a browser with no restriction on it.",
  ],
  verses: [
    {
      verse: {
        text: "But put on the Lord Jesus Christ, and make no provision for the flesh, for its lusts.",
        ref: "Romans 13:14",
      },
      note: "Make no provision is a supply-chain phrase: do not lay in stores for it, do not keep the cupboard stocked. It is about arrangements made in advance rather than resistance in the moment, which is the entire argument for closing the second door before you need to. A blocked app with an open browser is a well-stocked cupboard.",
    },
    {
      verse: {
        text: "Can a man scoop fire into his lap, and his clothes not be burned?",
        ref: "Proverbs 6:27",
      },
      note: "A rhetorical question about proximity rather than about intention, and it is the reason this page exists. The proverb does not ask whether you meant to be burned. It asks what you were holding, which is the more useful question to put to a phone with one door closed and one open.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "The two systems people confuse",
      id: "the-two-systems-people-confuse",
    },
    {
      type: "p",
      text: "App Limits are a stopwatch. Content Restrictions are a gate. They live in the same menu, they sound similar, and only one of them knows what a website is.",
    },
    {
      type: "p",
      text: "Nearly every story that ends with Screen Time does not work turns out on inspection to be a stopwatch being asked to do a gate's job.",
    },
    {
      type: "h2",
      text: "Name every address",
      id: "name-every-address",
    },
    {
      type: "p",
      text: "The Never Allow list matches addresses, not services, so a site with several is several entries. X needs both of its domains. Reddit has old and new. Instagram has a couple of paths people reach it by.",
    },
    {
      type: "note",
      text: "This is fiddly and takes ten minutes once. It is also the difference between a phone that holds and one that only appears to.",
    },
    {
      type: "h2",
      text: "The passcode again",
      id: "the-passcode-again",
    },
    {
      type: "p",
      text: "Without a Screen Time passcode, everything above is a preference rather than a boundary — three taps and it is off, and those three taps will happen at eleven at night rather than at eleven in the morning.",
    },
    {
      type: "p",
      text: "This is the same point {the article on Screen Time} makes at length, and it is the one that decides whether any of the rest of this works.",
      links: {
        "the article on Screen Time": "/blog/screen-time-limits-dont-work",
      },
    },
  ],
  faq: [
    {
      q: "How do I block a website on my iPhone?",
      a: "Settings → Screen Time → Content & Privacy Restrictions → Content Restrictions → Web Content → Limit Adult Websites, then add the address under Never Allow. It applies to every browser on the phone, not just Safari.",
    },
    {
      q: "Why can I still use an app I blocked?",
      a: "Because you blocked the app and not the site. App Limits count time inside apps and have nothing to say about a browser tab, so the mobile website of whatever you blocked is still fully available.",
    },
    {
      q: "Should I delete Safari to stop browsing?",
      a: "No — another browser is ten seconds away and it will have no restrictions on it. Content Restrictions work below the browser, which is why they survive that.",
    },
  ],
  relatedApps: ["reddit", "twitter"],
  related: ["screen-time-limits-dont-work", "christian-app-blocker-guide"],
  relatedPrayers: [
    "prayer-for-self-control",
    "prayer-before-you-open-your-phone",
  ],
};
